import { v4 as uuid } from "uuid";
import {
  indexedDBManager,
  type QueueItemDB,
} from "@/services/IndexedDBManager";
import { createAppAsyncThunk } from "@/store/createAppAsyncThunk";
import { createTicketRequest } from "../ticketApi";
import type { TicketFormBody } from "../ticketForm/ticketFormOperations";
import { MAX_QUEUE_RETRIES } from "./ticketQueueSlice";

// Reentrancy guard to prevent concurrent queue processing
let isProcessingQueue = false;

/**
 * Initialize queue - Load items from IndexedDB on app start
 */
export const initQueueThunk = createAppAsyncThunk<QueueItemDB[]>(
  "queue/init",
  async () => {
    await indexedDBManager.init();
    const items = await indexedDBManager.getAllQueueItems();
    return items;
  },
);

/**
 * Add new item to queue
 */
export const addQueueItemThunk = createAppAsyncThunk<
  QueueItemDB[],
  { data: Record<string, any> }
>("queue/add", async ({ data }) => {
  // create item in pending status
  const item: QueueItemDB = {
    id: uuid(),
    timestamp: Date.now(),
    status: "pending",
    retryCount: 0,
    data, // store entire payload
  };

  await indexedDBManager.saveQueueItem(item);

  return indexedDBManager.getAllQueueItems();
});

export const processQueueThunk = createAppAsyncThunk<QueueItemDB[], void>(
  "queue/process",
  async () => {
    // Skip if already processing
    if (isProcessingQueue) {
      return indexedDBManager.getAllQueueItems();
    }
    isProcessingQueue = true;
    try {
      if (!navigator.onLine) {
        console.log("🔴 Offline - Queue processing skipped");
        return indexedDBManager.getAllQueueItems();
      }

      const allItems =
        await indexedDBManager.getAllQueueItems<TicketFormBody>();
      const pendingItems = allItems.filter((item) => item.status === "pending");

      if (pendingItems.length === 0) {
        console.log("📭 Queue is empty");
        return allItems;
      }

      console.log(`📤 Processing ${pendingItems.length} pending items...`);

      const persistQueueItemUpdate = async (
        id: string,
        update: (queueItem: QueueItemDB<TicketFormBody>) => void,
      ) => {
        const dbItem = await indexedDBManager.getQueueItem<TicketFormBody>(id);

        if (!dbItem) {
          console.warn(`⚠️ Queue item ${id} missing from IndexedDB – skipping.`);
          return null;
        }

        const updatedItem: QueueItemDB<TicketFormBody> = { ...dbItem };
        update(updatedItem);
        await indexedDBManager.saveQueueItem(updatedItem);
        return updatedItem;
      };

      for (const item of pendingItems) {
        const queueItemId = item.id;

        try {
          const queueItemFromDb = await persistQueueItemUpdate(
            queueItemId,
            (dbItem) => {
              dbItem.status = "sending";
              dbItem.error = undefined;
            },
          );

          if (!queueItemFromDb) {
            continue;
          }

          const ticketBody = queueItemFromDb.data;
          console.log("Submitting ticket:", ticketBody);

          if (!ticketBody) {
            console.warn(
              `⚠️ Queue item ${queueItemId} has no payload – removing entry.`,
            );
            await indexedDBManager.deleteQueueItem(queueItemId);
            continue;
          }

          await createTicketRequest(ticketBody);

          await persistQueueItemUpdate(queueItemId, (dbItem) => {
            dbItem.status = "sent";
          });

          await indexedDBManager.deleteQueueItem(queueItemId);
        } catch (error) {
          console.error(`❌ Failed to submit item ${queueItemId}:`, error);

          await persistQueueItemUpdate(queueItemId, (dbItem) => {
            const nextRetryCount = dbItem.retryCount + 1;
            const reachedMaxRetries = nextRetryCount >= MAX_QUEUE_RETRIES;
            const errorMessage =
              error instanceof Error ? error.message : "Unknown error";

            dbItem.retryCount = nextRetryCount;

            if (reachedMaxRetries) {
              dbItem.status = "error";
              dbItem.error = errorMessage;
              return;
            }

            dbItem.status = "pending";
            dbItem.error = undefined;
          });
        }
      }

      return indexedDBManager.getAllQueueItems();
    } finally {
      isProcessingQueue = false;
    }
  },
);

/**
 * Retry a single failed queue item
 * Resets the item to pending status and clears error/retryCount
 */
export const retryQueueItemThunk = createAppAsyncThunk<QueueItemDB[], string>(
  "queue/retryItem",
  async (itemId) => {
    const item = await indexedDBManager.getQueueItem(itemId);

    if (!item) {
      console.warn(`⚠️ Queue item ${itemId} not found`);
      return indexedDBManager.getAllQueueItems();
    }

    // Reset to pending status
    const updatedItem: QueueItemDB = {
      ...item,
      status: "pending",
      retryCount: 0,
      error: undefined,
    };

    await indexedDBManager.saveQueueItem(updatedItem);
    console.log(`🔄 Queue item ${itemId} reset to pending`);

    return indexedDBManager.getAllQueueItems();
  },
);

/**
 * Delete a queue item manually
 */
export const deleteQueueItemThunk = createAppAsyncThunk<QueueItemDB[], string>(
  "queue/deleteItem",
  async (itemId) => {
    await indexedDBManager.deleteQueueItem(itemId);
    console.log(`🗑️ Queue item ${itemId} deleted by user`);

    return indexedDBManager.getAllQueueItems();
  },
);
